import { useState } from 'react';
import { DOMAIN_SUGGESTION_LIMIT, formatDomainSuggestion } from '../../services/creationRequestService';

/**
 * Gestion des domaines recommandés d'une demande (posés par l'équipe).
 * Affichés au client à « site prêt » dans le panneau de lancement.
 * Stockage : { name, extension } SÉPARÉS (règle anti « monsite.fr.fr ») —
 * le nom complet n'est construit qu'à l'affichage (formatDomainSuggestion).
 *
 * @param {Object} props
 * @param {Array<{name: string, extension: 'fr'|'com'}>} props.suggestions
 * @param {string|Object|null} props.domainWish - souhait saisi par le client
 * @param {(suggestion: {name: string, extension: string}) => void} props.onAdd
 * @param {(index: number) => void} props.onRemove
 * @param {boolean} props.isManaging
 */
const DomainSuggestionsManager = ({ suggestions = [], domainWish = null, onAdd, onRemove, isManaging = false }) => {
  const [name, setName] = useState('');
  const [extension, setExtension] = useState('fr');
  const [error, setError] = useState(null);

  // Souhait du client : chaîne libre (étape 5) ou ancien format { name, extension }
  const clientWish = typeof domainWish === 'string'
    ? domainWish.trim()
    : formatDomainSuggestion(domainWish);

  const remaining = Math.max(0, DOMAIN_SUGGESTION_LIMIT - suggestions.length);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Saisissez le nom du domaine (sans l'extension).");
      return;
    }
    setError(null);
    onAdd({ name: name.trim(), extension });
    setName('');
  };

  return (
    <div className="space-y-3">
      {clientWish && (
        <div className="alert alert-info py-2 text-sm">
          <span>
            💡 Souhait du client : <span className="font-mono font-medium">{clientWish}</span> — à
            vérifier en priorité si disponible.
          </span>
        </div>
      )}

      <form onSubmit={handleAdd} className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            className="input input-bordered input-sm flex-1 min-w-[180px]"
            placeholder="Nom du domaine (ex : monsite)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={remaining === 0}
          />
          <select
            className="select select-bordered select-sm w-28"
            value={extension}
            onChange={(e) => setExtension(e.target.value)}
            disabled={remaining === 0}
          >
            <option value="fr">.fr</option>
            <option value="com">.com</option>
          </select>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={isManaging || remaining === 0}
            title={remaining === 0 ? '3 domaines maximum' : 'Ajouter la suggestion'}
          >
            {isManaging ? 'Ajout...' : 'Ajouter'}
          </button>
        </div>
        <p className="text-xs text-base-content/50">
          {remaining > 0
            ? `${remaining} suggestion${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''} (3 max) · .fr ou .com`
            : '3 domaines maximum atteint.'}
        </p>
        {error && <p className="text-xs text-error">{error}</p>}
      </form>

      {suggestions.length === 0 && (
        <p className="text-xs text-base-content/50 italic">
          Aucun domaine recommandé pour le moment. Ces propositions seront affichées au client quand
          son site sera prêt.
        </p>
      )}

      <div className="space-y-2">
        {suggestions.map((suggestion, index) => (
          <div
            key={index}
            className="flex items-center gap-3 border border-base-200 rounded-lg p-2"
          >
            <span className="flex-1 text-sm font-mono text-base-content break-all">
              {formatDomainSuggestion(suggestion) || `Suggestion ${index + 1}`}
            </span>
            <button
              type="button"
              className="btn btn-xs btn-ghost text-error"
              onClick={() => onRemove(index)}
              disabled={isManaging}
              title="Retirer cette suggestion"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DomainSuggestionsManager;
